import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Video, RefreshCw, X } from 'lucide-react';
import { fetchSiteSettings, updateSiteSettings } from './hooks/hooks';

const NAV = '#001f3f';

// Accepts a bare video ID or a full YouTube URL (watch/share/embed forms) and
// returns just the ID, so admins can paste whatever they copied from YouTube.
function extractYoutubeId(input) {
  const value = input.trim();
  const urlMatch = value.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{6,32})/
  );
  return urlMatch ? urlMatch[1] : value;
}

const Toast = ({ toast, onClose }) => {
  if (!toast.open) return null;
  const cls = toast.severity === 'success'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-red-50 text-red-700 border-red-200';
  return (
    <div className="fixed top-4 right-4 z-50">
      <div className={`${cls} border rounded-lg px-4 py-3 shadow-lg flex items-center gap-2 max-w-sm`}>
        <span className="text-sm font-medium">{toast.message}</span>
        <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-600">
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

function WebsiteSettings() {
  const queryClient = useQueryClient();
  const { data: settings, error, isLoading, refetch } = useQuery({
    queryKey: ['site-settings'],
    queryFn: fetchSiteSettings,
  });

  const [videoInput, setVideoInput] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: '' });

  useEffect(() => {
    if (settings) setVideoInput(settings.home_video_youtube_id || '');
  }, [settings]);

  const mutation = useMutation({
    mutationFn: updateSiteSettings,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      setVideoInput(data.home_video_youtube_id || '');
      setToast({ open: true, message: 'Homepage video updated.', severity: 'success' });
    },
    onError: () => setToast({ open: true, message: 'Failed to update video. Try again.', severity: 'error' }),
  });

  const handleSave = () => {
    mutation.mutate({ home_video_youtube_id: extractYoutubeId(videoInput) });
  };

  const previewId = extractYoutubeId(videoInput);

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md">
          <h3 className="text-sm font-bold text-red-800 mb-1">Error Loading Settings</h3>
          <p className="text-sm text-red-600">Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Website Settings</h2>
          <p className="text-xs text-slate-400 mt-0.5">Controls for the corporate website (jamboricg-web).</p>
        </div>
        <button onClick={refetch} className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40 text-slate-400">Loading…</div>
      ) : (
        <div className="max-w-2xl bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-2">
            <Video size={16} style={{ color: NAV }} />
            <div>
              <h3 className="text-sm font-bold text-slate-800">Homepage Video</h3>
              <p className="text-xs text-slate-400">Paste a YouTube video ID or link — it replaces the video shown on the website's home page.</p>
            </div>
          </div>

          <div className="p-6 space-y-5">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">YouTube Video ID or URL</label>
              <input
                value={videoInput}
                onChange={(e) => setVideoInput(e.target.value)}
                placeholder="e.g. RM4AYBBHLZg or https://youtu.be/RM4AYBBHLZg"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {previewId && (
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Preview</label>
                <div className="relative w-full rounded-lg overflow-hidden bg-black" style={{ paddingTop: '56.25%' }}>
                  <iframe
                    key={previewId}
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${previewId}`}
                    title="Homepage video preview"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={handleSave}
              disabled={mutation.isLoading}
              className="w-full bg-teal-600 text-white py-2.5 px-6 rounded-lg font-semibold text-sm hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              {mutation.isLoading ? 'Saving...' : 'Save Video'}
            </button>
          </div>
        </div>
      )}

      <Toast toast={toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />
    </div>
  );
}

export default WebsiteSettings;
